import { useEffect, useMemo, useRef, useState } from "react";
import {
  CheckCircle2,
  Copy,
  ExternalLink,
  Eye,
  EyeOff,
  Info,
  Link2,
  Loader2,
  Plus,
  RefreshCw,
  Settings,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import Layout, { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/Cards";
import { Button } from "@/components/ui/Buttons";
import { Input } from "@/components/ui/Inputs";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import {
  usePmIntegrations,
  useCreatePmIntegration,
  useUpdatePmIntegration,
  useDeletePmIntegration,
  useTestPmIntegration,
} from "@/hooks/usePmIntegrations";
import type { PmIntegration, PmIntegrationTestResult, PmProvider } from "@/lib/api";

// ---------------------------------------------------------------------------
// Per-provider form spec — what we ask the user for when connecting.
// Keep this small; per-tool quirks live in the connector adapter, not here.
// ---------------------------------------------------------------------------

interface ProviderFormSpec {
  /** What the workspace ID actually is in this tool's vocabulary. */
  workspaceLabel: string;
  workspaceHelper: string;
  /** Field defs for the credentials object. */
  credentialFields: Array<{
    key: string;
    label: string;
    helper?: string;
    type: "password" | "text";
  }>;
  /** Optional default project label. */
  defaultProjectLabel?: string;
  defaultProjectHelper?: string;
}

const PROVIDER_FORMS: Partial<Record<PmProvider, ProviderFormSpec>> = {
  teamwork: {
    workspaceLabel: "Teamwork subdomain",
    workspaceHelper:
      "The subdomain part of your Teamwork URL — e.g. for callaloo.teamwork.com, enter just `callaloo`.",
    credentialFields: [
      {
        key: "apiKey",
        label: "API token",
        type: "password",
        helper: "Profile → Edit my details → API & Mobile to view your personal API token.",
      },
    ],
    defaultProjectLabel: "Default Tasklist ID",
    defaultProjectHelper:
      "Tasklist that new tasks should be created in. You can override per project later.",
  },
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function SettingsIntegrations() {
  const { data, isLoading, error } = usePmIntegrations();
  const createMut = useCreatePmIntegration();
  const updateMut = useUpdatePmIntegration();
  const deleteMut = useDeletePmIntegration();

  const [connectingProvider, setConnectingProvider] = useState<PmProvider | null>(null);
  const [editingIntegration, setEditingIntegration] = useState<PmIntegration | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PmIntegration | null>(null);

  const integrations = data?.integrations ?? [];
  const availableProviders = data?.availableProviders ?? [];

  const integrationsByProvider = useMemo(() => {
    const m = new Map<PmProvider, PmIntegration[]>();
    for (const i of integrations) {
      const list = m.get(i.provider) ?? [];
      list.push(i);
      m.set(i.provider, list);
    }
    return m;
  }, [integrations]);

  if (isLoading) {
    return (
      <Layout>
        <PageHeader title="Integrations" subtitle="Loading…" />
        <div className="flex items-center justify-center py-24">
          <Loader2 size={20} className="animate-spin text-orange-400" />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <PageHeader title="Integrations" subtitle="Couldn't load" />
        <div className="p-8">
          <Card>
            <div className="text-sm text-red-400 font-medium">{(error as Error).message}</div>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHeader
        title="Integrations"
        subtitle="Connect Callaloo to your project-management tools so deliverables sync end-to-end."
      />

      <div className="p-8 pb-24 space-y-6 max-w-3xl">
        <Card>
          <div className="flex items-start gap-3">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
              style={{ backgroundColor: "rgba(249,115,22,0.1)" }}
            >
              <Info size={16} style={{ color: "#F97316" }} />
            </div>
            <div className="text-xs" style={{ color: "#9CA3AF" }}>
              <p className="text-white text-sm font-medium mb-1">How sync works</p>
              <p>
                When you connect a project-management tool, deliverable and attachment requirements
                can be pushed to it as tasks. Once a requirement is synced, status and assignee
                become managed there — Callaloo reflects the change but doesn't override it.
                Compliance, admin, and pricing items stay in Callaloo only.
              </p>
            </div>
          </div>
        </Card>

        {/* One card per registered provider, even if not yet connected. */}
        {availableProviders.map((p) => {
          const existing = integrationsByProvider.get(p.provider) ?? [];
          return (
            <ProviderCard
              key={p.provider}
              provider={p.provider}
              displayName={p.displayName}
              integrations={existing}
              onConnect={() => setConnectingProvider(p.provider)}
              onEdit={(i) => setEditingIntegration(i)}
              onDelete={(i) => setPendingDelete(i)}
              onToggleEnabled={(i) =>
                updateMut.mutate({ id: i.id, patch: { enabled: !i.enabled } })
              }
            />
          );
        })}

        {availableProviders.length === 0 && (
          <Card>
            <div className="text-sm text-gray-400 py-4 text-center">
              No PM-tool connectors are registered on this server. Ask your admin.
            </div>
          </Card>
        )}
      </div>

      {connectingProvider && (
        <ConnectIntegrationModal
          provider={connectingProvider}
          providerDisplayName={
            availableProviders.find((p) => p.provider === connectingProvider)?.displayName ??
            connectingProvider
          }
          onClose={() => setConnectingProvider(null)}
          onSubmit={async (input) => {
            try {
              await createMut.mutateAsync(input);
              toast.success("Connected.");
              setConnectingProvider(null);
            } catch (err) {
              toast.error((err as Error).message);
            }
          }}
          submitting={createMut.isPending}
        />
      )}

      {editingIntegration && (
        <EditIntegrationModal
          integration={editingIntegration}
          onClose={() => setEditingIntegration(null)}
          onSubmit={async (patch) => {
            try {
              await updateMut.mutateAsync({
                id: editingIntegration.id,
                patch,
              });
              toast.success("Updated.");
              setEditingIntegration(null);
            } catch (err) {
              toast.error((err as Error).message);
            }
          }}
          submitting={updateMut.isPending}
        />
      )}

      <ConfirmDeleteDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => {
          if (!o) setPendingDelete(null);
        }}
        title="Disconnect this integration?"
        description={
          pendingDelete
            ? `Tasks already created in ${pendingDelete.displayName} won't be deleted, but Callaloo will stop syncing them. You can reconnect later.`
            : ""
        }
        confirmLabel="Disconnect"
        onConfirm={async () => {
          if (!pendingDelete) return;
          try {
            await deleteMut.mutateAsync(pendingDelete.id);
            toast.success("Disconnected.");
            setPendingDelete(null);
          } catch (err) {
            toast.error((err as Error).message);
          }
        }}
        busy={deleteMut.isPending}
      />
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// Provider card — one per supported tool, lists existing connections + Connect button.
// ---------------------------------------------------------------------------

function ProviderCard({
  provider,
  displayName,
  integrations,
  onConnect,
  onEdit,
  onDelete,
  onToggleEnabled,
}: {
  provider: PmProvider;
  displayName: string;
  integrations: PmIntegration[];
  onConnect: () => void;
  onEdit: (i: PmIntegration) => void;
  onDelete: (i: PmIntegration) => void;
  onToggleEnabled: (i: PmIntegration) => void;
}) {
  return (
    <Card>
      <div className="flex items-center gap-3 mb-3">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: "rgba(255,255,255,0.04)" }}
        >
          <Link2 size={18} className="text-gray-300" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-white">{displayName}</h3>
          <p className="text-xs" style={{ color: "#9CA3AF" }}>
            {integrations.length === 0
              ? "Not connected"
              : `${integrations.length} connection${integrations.length === 1 ? "" : "s"}`}
          </p>
        </div>
        <Button variant="secondary" size="sm" icon={<Plus size={14} />} onClick={onConnect}>
          {integrations.length === 0 ? "Connect" : "Add another"}
        </Button>
      </div>

      {integrations.length > 0 && (
        <div className="space-y-2 mt-2">
          {integrations.map((i) => (
            <ConnectedIntegrationRow
              key={i.id}
              integration={i}
              onEdit={() => onEdit(i)}
              onDelete={() => onDelete(i)}
              onToggleEnabled={() => onToggleEnabled(i)}
            />
          ))}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// One connected integration — workspace + webhook config + test/edit/delete.
// ---------------------------------------------------------------------------

function ConnectedIntegrationRow({
  integration,
  onEdit,
  onDelete,
  onToggleEnabled,
}: {
  integration: PmIntegration;
  onEdit: () => void;
  onDelete: () => void;
  onToggleEnabled: () => void;
}) {
  const [showSecret, setShowSecret] = useState(false);
  const testMut = useTestPmIntegration();

  const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";
  const webhookUrl = `${apiBase || window.location.origin}/api/v1/webhooks/pm/${integration.provider}?integration=${integration.id}`;

  const copy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied.`);
    } catch {
      toast.info(`Copy this: ${value}`);
    }
  };

  return (
    <div
      className="rounded-lg border px-3 py-3 space-y-3"
      style={{
        backgroundColor: "#161B22",
        borderColor: "rgba(229,231,235,0.06)",
      }}
    >
      {/* Header line: name, status pill, action menu */}
      <div className="flex items-center gap-3">
        <span
          className="w-1.5 h-1.5 rounded-full flex-shrink-0"
          style={{
            backgroundColor: integration.enabled ? "#22c55e" : "#9CA3AF",
          }}
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-white font-medium truncate">{integration.displayName}</p>
          <p className="text-xs" style={{ color: "#9CA3AF" }}>
            Workspace: <span className="text-gray-300">{integration.providerWorkspaceId}</span>
            {integration.providerDefaultProjectId && (
              <>
                {" · "}Default project:{" "}
                <span className="text-gray-300">{integration.providerDefaultProjectId}</span>
              </>
            )}
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          icon={<RefreshCw size={12} />}
          onClick={() => {
            testMut.mutate(integration.id, {
              onSuccess(result: PmIntegrationTestResult) {
                if (result.ok) toast.success(result.message);
                else toast.error(result.message);
              },
              onError(err) {
                toast.error((err as Error).message);
              },
            });
          }}
          loading={testMut.isPending}
        >
          Test
        </Button>
        <Button variant="ghost" size="sm" icon={<Settings size={12} />} onClick={onEdit}>
          Edit
        </Button>
        <button
          onClick={onToggleEnabled}
          className="text-xs text-gray-400 hover:text-white px-2 py-1 rounded-md hover:bg-white/5"
          title={integration.enabled ? "Disable" : "Enable"}
        >
          {integration.enabled ? "Disable" : "Enable"}
        </button>
        <button
          onClick={onDelete}
          className="text-gray-400 hover:text-red-400 p-1.5 rounded-lg hover:bg-red-500/10"
          title="Disconnect"
        >
          <Trash2 size={14} />
        </button>
      </div>

      {/* Webhook config — what to paste into the external tool. */}
      <div
        className="rounded-md border px-3 py-2 space-y-2"
        style={{
          backgroundColor: "rgba(255,255,255,0.02)",
          borderColor: "rgba(229,231,235,0.05)",
        }}
      >
        <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: "#9CA3AF" }}>
          Webhook config — paste these into {integration.displayName}
        </p>
        <CopyableField
          label="URL"
          value={webhookUrl}
          onCopy={() => copy(webhookUrl, "Webhook URL")}
        />
        <CopyableField
          label="Secret"
          value={
            showSecret
              ? (integration.webhookSecret ?? "")
              : integration.webhookSecret
                ? "•".repeat(48)
                : "(not set)"
          }
          monospace
          onCopy={() =>
            integration.webhookSecret && copy(integration.webhookSecret, "Webhook secret")
          }
          actionsRight={
            <button
              onClick={() => setShowSecret((v) => !v)}
              className="text-gray-400 hover:text-white p-1 rounded-md hover:bg-white/5"
              title={showSecret ? "Hide" : "Reveal"}
            >
              {showSecret ? <EyeOff size={12} /> : <Eye size={12} />}
            </button>
          }
        />
        <p className="text-[11px]" style={{ color: "#6B7280" }}>
          In {integration.displayName}, sign payloads with this secret using HMAC-SHA1 and POST
          events to the URL above.{" "}
          <a
            href="#"
            className="underline hover:text-orange-400"
            onClick={(e) => e.preventDefault()}
          >
            Setup guide
          </a>
        </p>
      </div>
    </div>
  );
}

function CopyableField({
  label,
  value,
  onCopy,
  actionsRight,
  monospace,
}: {
  label: string;
  value: string;
  onCopy: () => void;
  actionsRight?: React.ReactNode;
  monospace?: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <span
        className="text-[10px] uppercase tracking-wider font-semibold w-12 flex-shrink-0"
        style={{ color: "#6B7280" }}
      >
        {label}
      </span>
      <code
        className={`flex-1 min-w-0 text-xs px-2 py-1 rounded bg-white/5 border border-white/10 truncate ${monospace ? "font-mono" : ""}`}
        style={{ color: "#D1D5DB" }}
      >
        {value}
      </code>
      <button
        onClick={onCopy}
        className="text-gray-400 hover:text-white p-1 rounded-md hover:bg-white/5"
        title="Copy"
      >
        <Copy size={12} />
      </button>
      {actionsRight}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Connect modal — first-time setup, asks workspace + credentials.
// ---------------------------------------------------------------------------

function ConnectIntegrationModal({
  provider,
  providerDisplayName,
  onClose,
  onSubmit,
  submitting,
}: {
  provider: PmProvider;
  providerDisplayName: string;
  onClose: () => void;
  onSubmit: (input: {
    provider: PmProvider;
    providerWorkspaceId: string;
    providerDefaultProjectId?: string | null;
    displayName: string;
    credentials: Record<string, unknown>;
  }) => Promise<void>;
  submitting: boolean;
}) {
  const spec = PROVIDER_FORMS[provider];
  const [displayName, setDisplayName] = useState(providerDisplayName);
  const [workspaceId, setWorkspaceId] = useState("");
  const [defaultProjectId, setDefaultProjectId] = useState("");
  const [credentialValues, setCredentialValues] = useState<Record<string, string>>({});
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  if (!spec) {
    // Provider is registered server-side but we don't have a form spec for
    // it yet. Render a minimal apology.
    return (
      <ModalShell onClose={onClose}>
        <h3 className="text-sm font-semibold text-white mb-2">
          {providerDisplayName} setup not yet configured
        </h3>
        <p className="text-xs text-gray-400">
          We support {providerDisplayName} on the server side, but the connect form for it isn't
          built yet. Please reach out to the Callaloo team.
        </p>
      </ModalShell>
    );
  }

  const canSubmit =
    Boolean(workspaceId.trim()) &&
    spec.credentialFields.every((f) => Boolean((credentialValues[f.key] ?? "").trim()));

  const handleSubmit = async () => {
    if (!canSubmit) return;
    await onSubmit({
      provider,
      providerWorkspaceId: workspaceId.trim(),
      providerDefaultProjectId: defaultProjectId.trim() || null,
      displayName: displayName.trim() || providerDisplayName,
      credentials: credentialValues,
    });
  };

  return (
    <ModalShell onClose={onClose}>
      <div ref={modalRef}>
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h3 className="text-sm font-semibold text-white">Connect {providerDisplayName}</h3>
            <p className="text-xs mt-0.5" style={{ color: "#9CA3AF" }}>
              Credentials live with this org and aren't shared with other orgs on Callaloo.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white p-1.5 rounded-lg hover:bg-white/5"
          >
            <X size={16} />
          </button>
        </div>

        <div className="space-y-3">
          <Input
            label="Display name"
            placeholder={providerDisplayName}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
          <div>
            <Input
              label={spec.workspaceLabel}
              placeholder="callaloo"
              value={workspaceId}
              onChange={(e) => setWorkspaceId(e.target.value)}
            />
            <p className="text-[11px] mt-1 flex items-start gap-1.5" style={{ color: "#9CA3AF" }}>
              <Info size={11} className="mt-0.5 flex-shrink-0" />
              <span>{spec.workspaceHelper}</span>
            </p>
          </div>
          {spec.credentialFields.map((f) => (
            <div key={f.key}>
              <Input
                label={f.label}
                type={f.type}
                placeholder={f.type === "password" ? "••••••••" : ""}
                value={credentialValues[f.key] ?? ""}
                onChange={(e) =>
                  setCredentialValues((prev) => ({
                    ...prev,
                    [f.key]: e.target.value,
                  }))
                }
              />
              {f.helper && (
                <p
                  className="text-[11px] mt-1 flex items-start gap-1.5"
                  style={{ color: "#9CA3AF" }}
                >
                  <Info size={11} className="mt-0.5 flex-shrink-0" />
                  <span>{f.helper}</span>
                </p>
              )}
            </div>
          ))}
          {spec.defaultProjectLabel && (
            <div>
              <Input
                label={`${spec.defaultProjectLabel} (optional)`}
                placeholder=""
                value={defaultProjectId}
                onChange={(e) => setDefaultProjectId(e.target.value)}
              />
              {spec.defaultProjectHelper && (
                <p
                  className="text-[11px] mt-1 flex items-start gap-1.5"
                  style={{ color: "#9CA3AF" }}
                >
                  <Info size={11} className="mt-0.5 flex-shrink-0" />
                  <span>{spec.defaultProjectHelper}</span>
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 mt-5">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            icon={<CheckCircle2 size={14} />}
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            loading={submitting}
          >
            Connect
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

// ---------------------------------------------------------------------------
// Edit modal — change display name, default project, or rotate credentials.
// ---------------------------------------------------------------------------

function EditIntegrationModal({
  integration,
  onClose,
  onSubmit,
  submitting,
}: {
  integration: PmIntegration;
  onClose: () => void;
  onSubmit: (
    patch: Partial<{
      displayName: string;
      providerWorkspaceId: string;
      providerDefaultProjectId: string | null;
      credentials: Record<string, unknown>;
    }>,
  ) => Promise<void>;
  submitting: boolean;
}) {
  const spec = PROVIDER_FORMS[integration.provider];
  const [displayName, setDisplayName] = useState(integration.displayName);
  const [workspaceId, setWorkspaceId] = useState(integration.providerWorkspaceId);
  const [defaultProjectId, setDefaultProjectId] = useState(
    integration.providerDefaultProjectId ?? "",
  );
  const [rotateCredentials, setRotateCredentials] = useState(false);
  const [credentialValues, setCredentialValues] = useState<Record<string, string>>({});

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const handleSubmit = async () => {
    const patch: Parameters<typeof onSubmit>[0] = {
      displayName: displayName.trim() || integration.displayName,
      providerWorkspaceId: workspaceId.trim() || integration.providerWorkspaceId,
      providerDefaultProjectId: defaultProjectId.trim() || null,
    };
    if (rotateCredentials && Object.keys(credentialValues).length > 0) {
      patch.credentials = credentialValues;
    }
    await onSubmit(patch);
  };

  return (
    <ModalShell onClose={onClose}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <h3 className="text-sm font-semibold text-white">Edit {integration.displayName}</h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white p-1.5 rounded-lg hover:bg-white/5"
        >
          <X size={16} />
        </button>
      </div>

      <div className="space-y-3">
        <Input
          label="Display name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
        />
        <Input
          label={spec?.workspaceLabel ?? "Workspace"}
          value={workspaceId}
          onChange={(e) => setWorkspaceId(e.target.value)}
        />
        <Input
          label={`${spec?.defaultProjectLabel ?? "Default project"} (optional)`}
          value={defaultProjectId}
          onChange={(e) => setDefaultProjectId(e.target.value)}
        />

        <label className="flex items-center gap-2 text-xs text-gray-300 pt-1">
          <input
            type="checkbox"
            checked={rotateCredentials}
            onChange={(e) => setRotateCredentials(e.target.checked)}
            className="accent-orange-500"
          />
          Rotate credentials
        </label>

        {rotateCredentials &&
          spec?.credentialFields?.map((f) => (
            <Input
              key={f.key}
              label={f.label}
              type={f.type}
              placeholder={f.type === "password" ? "••••••••" : ""}
              value={credentialValues[f.key] ?? ""}
              onChange={(e) =>
                setCredentialValues((prev) => ({
                  ...prev,
                  [f.key]: e.target.value,
                }))
              }
            />
          ))}
      </div>

      <div className="flex items-center justify-end gap-2 mt-5">
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          icon={<CheckCircle2 size={14} />}
          onClick={handleSubmit}
          loading={submitting}
        >
          Save
        </Button>
      </div>
    </ModalShell>
  );
}

// ---------------------------------------------------------------------------
// Modal shell — backdrop + centered card.
// ---------------------------------------------------------------------------

function ModalShell({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <>
      <div
        className="fixed inset-0 z-30"
        style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="fixed inset-0 z-40 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="rounded-xl border max-w-md w-full pointer-events-auto"
          style={{
            backgroundColor: "#0F1419",
            borderColor: "rgba(229,231,235,0.08)",
            boxShadow: "0 24px 64px rgba(0,0,0,0.55)",
          }}
        >
          <div className="px-5 py-5">{children}</div>
        </div>
      </div>
    </>
  );
}

// Unused for now — kept exported in case the page wants a dedicated header.
export { ExternalLink };
